import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faSortAmountUp,
  faSortAmountDown,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useNotificationQueryContext } from "../../context/NotificationQueryContext";

/**
 * NotificationFilters Component
 *
 * Provides filtering and sorting controls for notifications
 * Supports filtering by type, read status, and sorting by date
 */
const NotificationFilters = ({
  showTypeFilter = true,
  showReadFilter = true,
  showSortOptions = true,
  className = "",
}) => {
  const { filters, updateFilters, clearFilters } =
    useNotificationQueryContext();

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      [filterType]: value === "all" ? null : value,
    };

    updateFilters(newFilters);
  };

  // Handle sort change
  const handleSortChange = (sortValue) => {
    updateFilters({ sort: sortValue });
  };

  // Clear all filters
  const handleClearFilters = () => {
    clearFilters();
  };

  // Check if any filters are active
  const hasActiveFilters = filters.type !== null || filters.read !== null;

  return (
    <div className={`notification-filters ${className}`}>
      <div className="flex flex-wrap items-center gap-4">
        {/* Filter Icon */}
        <div className="flex items-center text-sm font-medium text-dark-grey">
          <FontAwesomeIcon icon={faFilter} className="mr-2 text-deep-red" />
          Filters:
        </div>

        {/* Type Filter */}
        {showTypeFilter && (
          <div className="flex items-center">
            <label htmlFor="type-filter" className="sr-only">
              Filter by type
            </label>
            <select
              id="type-filter"
              value={filters.type || "all"}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              className="input text-sm"
            >
              <option value="all">All types</option>
              <option value="follow">Follow notifications</option>
            </select>
          </div>
        )}

        {/* Read Status Filter */}
        {showReadFilter && (
          <div className="flex items-center">
            <label htmlFor="read-filter" className="sr-only">
              Filter by read status
            </label>
            <select
              id="read-filter"
              value={
                filters.read === null
                  ? "all"
                  : filters.read === true
                  ? "read"
                  : "unread"
              }
              onChange={(e) => {
                const value =
                  e.target.value === "all"
                    ? null
                    : e.target.value === "read"
                    ? true
                    : false;
                handleFilterChange("read", value);
              }}
              className="input text-sm"
            >
              <option value="all">All notifications</option>
              <option value="unread">Unread only</option>
              <option value="read">Read only</option>
            </select>
          </div>
        )}

        {/* Sort Options */}
        {showSortOptions && (
          <div className="flex items-center">
            <label htmlFor="sort-filter" className="sr-only">
              Sort notifications
            </label>
            <select
              id="sort-filter"
              value={filters.sort}
              onChange={(e) => handleSortChange(e.target.value)}
              className="input text-sm"
            >
              <option value="createdAt:desc">Newest first</option>
              <option value="createdAt:asc">Oldest first</option>
            </select>

            {/* Sort direction indicator */}
            <div className="ml-2 text-medium-grey">
              {filters.sort === "createdAt:desc" ? (
                <FontAwesomeIcon icon={faSortAmountDown} title="Descending" />
              ) : (
                <FontAwesomeIcon icon={faSortAmountUp} title="Ascending" />
              )}
            </div>
          </div>
        )}

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="btn btn--ghost text-sm"
            title="Clear all filters"
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="ml-1">Clear filters</span>
          </button>
        )}
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-medium-grey">Active filters:</span>

          {filters.type && (
            <span className="chip chip--info">
              Type: {filters.type}
              <button
                onClick={() => handleFilterChange("type", null)}
                className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none"
                title="Remove type filter"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          )}

          {filters.read !== null && (
            <span className="chip chip--ok">
              Status: {filters.read ? "Read" : "Unread"}
              <button
                onClick={() => handleFilterChange("read", null)}
                className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-600 focus:outline-none"
                title="Remove status filter"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationFilters;
